import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import InputField from "components/fields/InputField";
import Modal from "components/modal";
import { RiErrorWarningFill } from "react-icons/ri";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { editContainer } from "store/containerSlice";

function EditContainers({ containerDetails, refreshList, isOpen, onClose }) {
  const dispatch = useDispatch();
  const { editLoading, editError } = useSelector((state) => state.containers);

  function convertEta(input) {
    // input: "25-10-2025 10:06 PM"
    const [datePart, timePart, meridian] = input.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    let [hours, minutes] = timePart.split(":").map(Number);

    // Convert 12-hour → 24-hour
    if (meridian === "PM" && hours !== 12) hours += 12;
    if (meridian === "AM" && hours === 12) hours = 0;

    // Pad helper
    const pad = (n) => String(n).padStart(2, "0");

    return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}`;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      departurePort: "",
      arrivalPort: "",
      eta: "",
      departureTimeDate: "",
    },
    mode: "onBlur",
  });
  useEffect(() => {
    if (isOpen && containerDetails) {
      reset({
        departurePort: containerDetails.departurePort ?? "",
        arrivalPort: containerDetails.arrivalPort ?? "",
        departureTimeDate: convertEta(containerDetails.departureTimeDate),
        eta: convertEta(containerDetails.eta),
      });
    }
  }, [isOpen, containerDetails, reset]);

  const closeModal = () => {
    reset();
    onClose?.();
  };

  const onSubmit = async (data) => {
    if (!containerDetails?._id) {
      return;
    }
    try {
      const payload = {
        containerId: containerDetails._id,
        departurePort: data.departurePort,
        arrivalPort: data.arrivalPort,
        departureTimeDate: data.departureTimeDate
          ? new Date(data.departureTimeDate).toISOString()
          : null,
        eta: data.eta ? new Date(data.eta).toISOString() : null,
      };

      const response = await dispatch(editContainer(payload));

      if (
        response?.payload?.statuscode === 200 ||
        response?.payload?.statuscode === 201
      ) {
        refreshList?.();
        closeModal();
      } else {
        console.error(
          "Edit failed:",
          response?.payload?.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Edit failed:", error);
    }
  };

  if (!containerDetails) return null;

  return (
    <Modal isOpen={isOpen} onClose={closeModal} className="max-w-md">
      <h2 className="flex items-center border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
        Edit Container #
        {editError ? (
          <div className="flex items-center gap-2 text-red-500">
            <RiErrorWarningFill className="h-4 w-4" />
          </div>
        ) : (
          containerDetails.containerNo
        )}
      </h2>

      <div className="mt-5 w-full max-w-4xl flex-col items-center lg:pl-0">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mx-auto grid w-full grid-cols-1 gap-4"
          noValidate
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
            <InputField
              id="edit-departurePort"
              variant="auth"
              label="Departure Port*"
              placeholder="Enter departure port"
              type="text"
              error={errors.departurePort}
              {...register("departurePort", {
                required: "Departure Port is required",
              })}
            />

            <InputField
              id="edit-arrivalPort"
              variant="auth"
              label="Arrival Port*"
              placeholder="Enter arrival port"
              type="text"
              error={errors.arrivalPort}
              {...register("arrivalPort", {
                required: "Arrival Port is required",
              })}
            />
            <InputField
              id="edit-departureTimeDate"
              variant="auth"
              label="Departure Time & Date*"
              placeholder="Select departure time & date"
              type="datetime-local"
              error={errors.departureTimeDate}
              {...register("departureTimeDate", {
                required: "Departure Time & Date is required",
              })}
            />

            <InputField
              id="edit-eta"
              variant="auth"
              label="ETA*"
              placeholder="Select ETA"
              type="datetime-local"
              error={errors.eta}
              {...register("eta", { required: "ETA is required" })}
            />
          </div>

          <div className="my-4 flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancel
            </Button>

            <LoaderButton type="submit" isLoading={editLoading}>
              Update Container
            </LoaderButton>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default EditContainers;
